/**
 * Nav v2 — provider-type resolver.
 *
 * Maps the platform's `OrgLabels.orgType` (`HOME_CARE | RESIDENTIAL |
 * MIXED`) to the prototype's `ProgramTag` vocabulary (`RAC | SAH`) used by
 * `nav-v2.ts` per-item filtering.
 *
 * Reference: `apps/web/src/contexts/org-labels-context.tsx`.
 *
 * Note on Phase 0 decisions.md Q6 inaccuracy: the original mapping table
 * referenced `residential_*` / `home_care_*` orgType strings. Live values
 * are the uppercase enum `HOME_CARE / RESIDENTIAL / MIXED`. The mapping
 * shape is the same — three discrete buckets — but the source strings are
 * different from what I documented. This resolver uses the live values.
 */

import {
  PROGRAM_FAMILIES,
  mapProgramSourceToken,
  type ProgramFamily,
} from '@statura/experience-contract';
import type { ProviderType } from './nav-v2';

export type { ProviderType } from './nav-v2';

export type OrgType = 'HOME_CARE' | 'RESIDENTIAL' | 'MIXED';

export const PROGRAM_LENS_COOKIE_NAME = 'statura_program_lens';
export const PROGRAM_LENS_QUERY_PARAM = 'program';
export const PROGRAM_LENS_REQUEST_HEADER = 'x-statura-program-lens';
export const WORKSPACE_LABEL_HOME_COMMUNITY = 'Home & community';
/**
 * Phase 2 rollout key. It is deliberately tenant-only: the server resolver
 * reads an exact `feature_flags.tenant_id` row and fails closed when absent.
 */
export const MIXED_WORKSPACE_FEATURE_FLAG = 'mixed_workspace_v1';

export type CurrentProviderProgramEnrollment = {
  id: unknown;
  client_id?: unknown;
  program_code: unknown;
  status?: unknown;
  start_date?: unknown;
  end_date?: unknown;
};

export type ProviderProgramLensOption = {
  family: ProgramFamily;
  firewallFamily: 'SAH' | 'RAC' | 'OTHER';
  label: string;
};

export type ProviderProgramResolutionStatus =
  | 'RESOLVED_SINGLE'
  | 'RESOLVED_DUAL'
  | 'RESOLVED_MULTI'
  | 'UNKNOWN';

export type ProviderProgramContext = {
  tenantId: string | null;
  providerType: ProviderType;
  resolutionStatus: ProviderProgramResolutionStatus;
  /** Server-derived discovery evidence; absence is not proof of an empty provider.
   * This marker does not resolve a family or grant client-data authority.
   * Verified registered setup can retain its family for a workforce-only workspace. */
  discoveryStatus?: 'CONFIRMED_EMPTY';
  activeProgramFamily: ProgramFamily | null;
  /** Phase 1 workspace. `null` preserves the legacy exact-family picker for
   * tenants that span residential and community firewalls. */
  workspace?: 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED' | null;
  /** Families admitted by the workspace (not an authority to pick a binding). */
  workspaceFamilies?: readonly ProgramFamily[];
  /** Firewall admission used by navigation and read grouping. */
  admittedFirewalls?: readonly ('SAH' | 'RAC' | 'OTHER')[];
  eligibleProgramFamilies: readonly ProgramFamily[];
  lensOptions: readonly ProviderProgramLensOption[];
  selectionRequired: boolean;
  selectionInvalid: boolean;
  selectionSource: 'AUTO' | 'URL' | 'COOKIE' | 'NONE';
  authorityRevision: string;
  contextRevision: string;
};

export type TrustedProgramMarkerFamily = 'SAH' | 'RAC' | 'OTHER' | 'MIXED' | 'SHARED' | 'UNKNOWN';

export const WORKSPACE_LABELS = {
  HOME_COMMUNITY: 'Home & community',
  RESIDENTIAL: 'Residential',
  MIXED: 'Mixed',
} as const;

/**
 * Workspace-aware family admission. A null workspace deliberately retains the
 * exact legacy lens semantics; it must never widen a RAC/community tenant.
 */
export function workspaceFamilies(context: ProviderProgramContext): readonly ProgramFamily[] {
  return context.workspace
    ? (context.workspaceFamilies ?? [])
    : context.activeProgramFamily
      ? [context.activeProgramFamily]
      : [];
}

/** Workspace-aware firewall admission with byte-identical legacy lens rules. */
export function workspaceHasFirewall(
  context: ProviderProgramContext,
  firewall: 'SAH' | 'RAC' | 'OTHER',
): boolean {
  if (context.workspace) return context.admittedFirewalls?.includes(firewall) === true;
  // Legacy exact-lens contexts: the accepted provider type must agree with the
  // active family's firewall, so an inconsistent context is never admitted.
  return (
    context.providerType === firewall &&
    context.lensOptions.find((option) => option.family === context.activeProgramFamily)
      ?.firewallFamily === firewall
  );
}

/**
 * CHSP is an OTHER-firewall family. A workspace admits the CHSP delivery
 * surfaces only when it admits that firewall and CHSP is one of its families,
 * so NDIS/DVA-only OTHER workspaces and Support at Home lenses never see them.
 */
export function workspaceAdmitsChsp(context: ProviderProgramContext): boolean {
  return workspaceHasFirewall(context, 'OTHER') && workspaceFamilies(context).includes('CHSP');
}

/**
 * Reduce the trusted server projection to the firewall family rendered by the
 * shell. URL and cookie hints are intentionally not inputs: they may request a
 * lens, but they cannot prove which programme context the application accepted.
 */
export function trustedProgramMarkerFamily(
  context: ProviderProgramContext,
): TrustedProgramMarkerFamily {
  if (context.workspace === 'MIXED' && !context.selectionRequired) return 'MIXED';
  if (!context.activeProgramFamily) {
    return context.selectionRequired ? 'SHARED' : 'UNKNOWN';
  }
  return (
    context.lensOptions.find((option) => option.family === context.activeProgramFamily)
      ?.firewallFamily ?? 'UNKNOWN'
  );
}

export type StoredProgramLens = {
  tenantId: string;
  actorUserId: string;
  family: ProgramFamily;
  authorityRevision: string;
};

export type StoredWorkspaceLens = {
  tenantId: string;
  actorUserId: string;
  workspace: 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED';
  authorityRevision: string;
};

export type ProgramLensHint =
  | { source: 'NONE' }
  | { source: 'URL'; value: unknown }
  | { source: 'COOKIE'; value: unknown };

const PROGRAM_FAMILY_LABELS: Record<ProgramFamily, string> = {
  SAH: 'Support at Home',
  HCP: 'Home Care Packages',
  CHSP: 'Commonwealth Home Support Programme',
  NDIS: 'NDIS',
  DVA: 'DVA',
  TAC: 'TAC',
  PRIVATE: 'Private services',
  BROKERAGE: 'Brokerage',
  PARTICIPANT_FUNDED: 'Participant funded',
  STRC: 'Short-Term Restorative Care',
  TRANSITION_CARE: 'Transition Care',
  NATSIFACP: 'NATSIFAC Programme',
  MPSP: 'Multi-Purpose Service Programme',
  FLEXIBLE_CARE: 'Flexible care',
  RAC: 'Residential aged care',
  OTHER: 'Other registered programme',
};

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function normalizeProgramLens(value: unknown): ProgramFamily | null {
  const normalized = nonEmptyString(value)?.toUpperCase() ?? null;
  return normalized && PROGRAM_FAMILIES.includes(normalized as ProgramFamily)
    ? (normalized as ProgramFamily)
    : null;
}

export function programFamilyLabel(family: ProgramFamily): string {
  return PROGRAM_FAMILY_LABELS[family];
}

export function encodeStoredProgramLens(selection: StoredProgramLens): string {
  return `v1:${selection.tenantId}:${selection.actorUserId}:${selection.family}:${selection.authorityRevision}`;
}

export function encodeStoredWorkspaceLens(selection: StoredWorkspaceLens): string {
  return `v2:${selection.tenantId}:${selection.actorUserId}:workspace:${selection.workspace}:${selection.authorityRevision}`;
}

/**
 * True when the stored lens is exactly the choice this context resolved for this person: same
 * tenant, same user, same authority revision and the same workspace or family. Used only to
 * decide whether a URL-chosen programme still needs remembering; it grants nothing.
 */
export function storedProgramLensMatchesContext(
  value: unknown,
  context: Pick<
    ProviderProgramContext,
    'tenantId' | 'activeProgramFamily' | 'workspace' | 'authorityRevision'
  >,
  actorUserId: string | null | undefined,
): boolean {
  const stored = parseStoredProgramLens(value);
  if (
    !stored ||
    !context.tenantId ||
    !actorUserId ||
    stored.tenantId !== context.tenantId ||
    stored.actorUserId !== actorUserId ||
    stored.authorityRevision !== context.authorityRevision
  )
    return false;
  return 'workspace' in stored
    ? Boolean(context.workspace) && stored.workspace === context.workspace
    : !context.workspace &&
        Boolean(context.activeProgramFamily) &&
        stored.family === context.activeProgramFamily;
}

export function parseStoredProgramLens(
  value: unknown,
): StoredProgramLens | StoredWorkspaceLens | null {
  const normalized = nonEmptyString(value);
  if (!normalized) return null;
  const familyMatch = /^v1:([0-9a-f-]{36}):([0-9a-f-]{36}):([A-Z_]+):([0-9a-f]{16})$/iu.exec(
    normalized,
  );
  if (familyMatch) {
    const tenantId = familyMatch[1];
    const actorUserId = familyMatch[2];
    const family = normalizeProgramLens(familyMatch[3]);
    const authorityRevision = familyMatch[4];
    if (!tenantId || !actorUserId || !family || !authorityRevision) return null;
    return {
      tenantId,
      actorUserId,
      family,
      authorityRevision,
    };
  }
  const workspaceMatch =
    /^v2:([0-9a-f-]{36}):([0-9a-f-]{36}):workspace:(HOME_COMMUNITY|RESIDENTIAL|MIXED):([0-9a-f]{16})$/iu.exec(
      normalized,
    );
  if (!workspaceMatch) return null;
  const tenantId = workspaceMatch[1];
  const actorUserId = workspaceMatch[2];
  const workspace = normalizeWorkspaceLens(`workspace:${workspaceMatch[3]}`);
  const authorityRevision = workspaceMatch[4];
  if (!tenantId || !actorUserId || !workspace || !authorityRevision) return null;
  return {
    tenantId,
    actorUserId,
    workspace,
    authorityRevision,
  };
}

export function normalizeWorkspaceLens(
  value: unknown,
): 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED' | null {
  const normalized = nonEmptyString(value)?.toUpperCase() ?? null;
  if (normalized === 'WORKSPACE:HOME_COMMUNITY') return 'HOME_COMMUNITY';
  if (normalized === 'WORKSPACE:RESIDENTIAL') return 'RESIDENTIAL';
  if (normalized === 'WORKSPACE:MIXED') return 'MIXED';
  return null;
}

/**
 * Stable, non-secret revision used to invalidate a persisted presentation
 * lens when current authoritative enrolments change. Authorisation still
 * comes from the fresh source query; this digest is only a stale-state guard.
 */
export function authorityRevisionForProgramRows(
  rows: readonly CurrentProviderProgramEnrollment[],
): string {
  const canonical = rows
    .filter((row) => !mapProgramSourceToken(row.program_code).overlay)
    .map((row) =>
      [row.id, row.client_id, row.program_code, row.start_date, row.end_date]
        .map((value) => (value === null || value === undefined ? '' : String(value)))
        .join('|'),
    )
    .sort()
    .join('\n');

  let high = 0x811c9dc5;
  let low = 0x9e3779b9;
  for (let index = 0; index < canonical.length; index += 1) {
    const code = canonical.charCodeAt(index);
    high = Math.imul(high ^ code, 0x01000193) >>> 0;
    low = Math.imul(low ^ (code + index), 0x85ebca6b) >>> 0;
  }
  return `${high.toString(16).padStart(8, '0')}${low.toString(16).padStart(8, '0')}`;
}

function closedProviderProgramContext(
  tenantId: string | null,
  authorityRevision: string,
  selectionInvalid = false,
): ProviderProgramContext {
  return {
    tenantId,
    providerType: 'UNKNOWN',
    resolutionStatus: 'UNKNOWN',
    activeProgramFamily: null,
    workspace: null,
    workspaceFamilies: [],
    admittedFirewalls: [],
    eligibleProgramFamilies: [],
    lensOptions: [],
    selectionRequired: false,
    selectionInvalid,
    selectionSource: 'NONE',
    authorityRevision,
    contextRevision: `${authorityRevision}:UNKNOWN`,
  };
}

export function constrainProviderProgramContextToTenant(
  context: ProviderProgramContext,
  tenantId: string,
): ProviderProgramContext {
  return context.tenantId === tenantId
    ? context
    : closedProviderProgramContext(tenantId, '0000000000000000');
}

export function evaluateProviderProgramContext(input: {
  tenantId: string | null;
  actorUserId?: string | null;
  rows: readonly CurrentProviderProgramEnrollment[];
  hint?: ProgramLensHint;
  authorityRevision?: string;
  mixedWorkspaceEnabled?: boolean;
  /**
   * Families offered from a LIVE tenant programme registration rather than a
   * current client enrolment (8 Oct 2026: first residential admission for a
   * home-care/mixed provider). Runtime callers must derive this server-side.
   * It adds a lens option only: it never enters the ACTIVE authority digest and
   * never admits a client to a firewall projection.
   */
  registeredSetupFamilies?: readonly 'RAC'[];
}): ProviderProgramContext {
  const authorityRevision = input.authorityRevision ?? authorityRevisionForProgramRows(input.rows);
  if (!input.tenantId) return closedProviderProgramContext(null, authorityRevision);

  const byFamily = new Map<ProgramFamily, ProviderProgramLensOption>();
  for (const row of input.rows) {
    if (typeof row.id !== 'string' || row.id.length === 0) {
      return closedProviderProgramContext(input.tenantId, authorityRevision);
    }
    const mapped = mapProgramSourceToken(row.program_code);
    if (mapped.overlay) continue;
    if (!mapped.family || mapped.firewallFamily === 'UNKNOWN') {
      return closedProviderProgramContext(input.tenantId, authorityRevision);
    }
    const existing = byFamily.get(mapped.family);
    if (existing && existing.firewallFamily !== mapped.firewallFamily) {
      return closedProviderProgramContext(input.tenantId, authorityRevision);
    }
    byFamily.set(mapped.family, {
      family: mapped.family,
      firewallFamily: mapped.firewallFamily,
      label: programFamilyLabel(mapped.family),
    });
  }
  if (input.registeredSetupFamilies?.includes('RAC') && !byFamily.has('RAC')) {
    byFamily.set('RAC', {
      family: 'RAC',
      firewallFamily: 'RAC',
      label: programFamilyLabel('RAC'),
    });
  }

  const lensOptions = [...byFamily.values()].sort((left, right) => {
    const priority = (family: ProgramFamily) => (family === 'SAH' ? 0 : family === 'RAC' ? 1 : 2);
    return priority(left.family) - priority(right.family) || left.label.localeCompare(right.label);
  });
  const eligibleProgramFamilies = lensOptions.map((option) => option.family);
  if (lensOptions.length === 0) {
    return closedProviderProgramContext(input.tenantId, authorityRevision);
  }

  // A workspace is a navigation/read grouping only. It deliberately does not
  // turn an OTHER binding into SAH authority (or vice versa): writes remain
  // bound to a client and an explicit firewall.
  const admittedFirewalls = [...new Set(lensOptions.map((option) => option.firewallFamily))].sort();
  const workspace = admittedFirewalls.every((family) => family === 'SAH' || family === 'OTHER')
    ? 'HOME_COMMUNITY'
    : admittedFirewalls.every((family) => family === 'RAC')
      ? 'RESIDENTIAL'
      : null;
  if (workspace) {
    // Legacy family resolution has no workspace selection contract, so a stored v2 workspace
    // lens selects nothing here. Like any stored lens that cannot apply, it is no stored
    // choice rather than an invalid request (see the stored-lens branches below).
    const selectionInvalid = false;
    const activeProgramFamily =
      lensOptions.length === 1
        ? lensOptions[0]!.family
        : // Deterministic compatibility marker only. Consumers must use
          // workspace/admittedFirewalls for workspace authority.
          (lensOptions.find((option) => option.family === 'SAH') ?? lensOptions[0]!).family;
    return {
      tenantId: input.tenantId,
      providerType:
        workspace === 'RESIDENTIAL' ? 'RAC' : admittedFirewalls.includes('SAH') ? 'SAH' : 'OTHER',
      resolutionStatus: 'RESOLVED_SINGLE',
      activeProgramFamily,
      workspace,
      workspaceFamilies: eligibleProgramFamilies,
      admittedFirewalls,
      eligibleProgramFamilies,
      lensOptions,
      selectionRequired: false,
      selectionInvalid,
      selectionSource: 'AUTO',
      authorityRevision,
      contextRevision: `${authorityRevision}:${workspace}`,
    };
  }

  // This pure evaluator is also used by isolated unit tests. Runtime callers
  // must supply the server-derived tenant value; omission remains false so a
  // client hint can never widen a legacy workspace.
  if (input.mixedWorkspaceEnabled ?? false) {
    const workspaceForHint = (candidate: 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED') => {
      if (candidate === 'MIXED') return lensOptions;
      return lensOptions.filter((option) =>
        candidate === 'RESIDENTIAL'
          ? option.firewallFamily === 'RAC'
          : option.firewallFamily !== 'RAC',
      );
    };
    let requestedWorkspace: 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED' | null = null;
    let selectionSource: ProviderProgramContext['selectionSource'] = 'AUTO';
    let selectionInvalid = false;
    const hint: ProgramLensHint = input.hint ?? { source: 'NONE' };
    if (hint.source === 'URL') {
      requestedWorkspace = normalizeWorkspaceLens(hint.value);
      selectionSource = requestedWorkspace ? 'URL' : 'AUTO';
      selectionInvalid = !requestedWorkspace && nonEmptyString(hint.value) !== null;
    } else if (hint.source === 'COOKIE') {
      const stored = parseStoredProgramLens(hint.value);
      if (
        stored &&
        'workspace' in stored &&
        stored.tenantId === input.tenantId &&
        stored.actorUserId === input.actorUserId &&
        stored.authorityRevision === authorityRevision
      ) {
        requestedWorkspace = stored.workspace;
        selectionSource = 'COOKIE';
      }
      // A stored lens that no longer matches (another tenant or user, a v1 family lens, or an
      // authority revision superseded by an enrolment change) is not something the person
      // asked for on this request: it is treated as no stored choice, never as an invalid one.
      // Only an explicit `?program=` in the URL can be invalid.
    }
    const selectedWorkspace = requestedWorkspace ?? 'MIXED';
    const selectedOptions = workspaceForHint(selectedWorkspace);
    if (selectedOptions.length === 0) {
      selectionInvalid = true;
      selectionSource = 'AUTO';
    }
    const effectiveWorkspace = selectedOptions.length === 0 ? 'MIXED' : selectedWorkspace;
    const effectiveOptions = selectedOptions.length === 0 ? lensOptions : selectedOptions;
    const effectiveFirewalls = [
      ...new Set(effectiveOptions.map((option) => option.firewallFamily)),
    ].sort();
    const firstEffectiveOption = effectiveOptions[0];
    if (!firstEffectiveOption)
      return closedProviderProgramContext(input.tenantId, authorityRevision);
    const activeProgramFamily =
      effectiveWorkspace === 'MIXED'
        ? null
        : (effectiveOptions.find((option) => option.family === 'SAH') ?? firstEffectiveOption)
            .family;
    return {
      tenantId: input.tenantId,
      providerType:
        effectiveWorkspace === 'MIXED'
          ? 'MIXED'
          : effectiveWorkspace === 'RESIDENTIAL'
            ? 'RAC'
            : effectiveFirewalls.includes('SAH')
              ? 'SAH'
              : 'OTHER',
      resolutionStatus: lensOptions.length === 2 ? 'RESOLVED_DUAL' : 'RESOLVED_MULTI',
      activeProgramFamily,
      workspace: effectiveWorkspace,
      workspaceFamilies: effectiveOptions.map((option) => option.family),
      admittedFirewalls: effectiveFirewalls,
      eligibleProgramFamilies,
      lensOptions,
      selectionRequired: false,
      selectionInvalid,
      selectionSource,
      authorityRevision,
      contextRevision: `${authorityRevision}:${effectiveWorkspace}`,
    };
  }

  const hint = input.hint ?? { source: 'NONE' as const };
  if (lensOptions.length === 1) {
    const selected = lensOptions[0]!;
    if (hint.source === 'URL') {
      const requested = normalizeProgramLens(hint.value);
      if (!requested || requested !== selected.family) {
        return {
          tenantId: input.tenantId,
          providerType: 'UNKNOWN',
          resolutionStatus: 'UNKNOWN',
          activeProgramFamily: null,
          eligibleProgramFamilies,
          lensOptions,
          selectionRequired: true,
          selectionInvalid: true,
          selectionSource: 'NONE',
          authorityRevision,
          contextRevision: `${authorityRevision}:SHARED`,
        };
      }
    }
    return {
      tenantId: input.tenantId,
      providerType: selected.firewallFamily,
      resolutionStatus: 'RESOLVED_SINGLE',
      activeProgramFamily: selected.family,
      eligibleProgramFamilies,
      lensOptions,
      selectionRequired: false,
      selectionInvalid: false,
      selectionSource: 'AUTO',
      authorityRevision,
      contextRevision: `${authorityRevision}:${selected.family}`,
    };
  }

  let selectedFamily: ProgramFamily | null = null;
  let selectionSource: ProviderProgramContext['selectionSource'] = 'NONE';
  let selectionInvalid = false;
  if (hint.source === 'URL') {
    selectedFamily = normalizeProgramLens(hint.value);
    selectionSource = 'URL';
    selectionInvalid = !selectedFamily;
  } else if (hint.source === 'COOKIE') {
    const stored = parseStoredProgramLens(hint.value);
    if (
      stored &&
      'family' in stored &&
      stored.tenantId === input.tenantId &&
      stored.actorUserId === input.actorUserId &&
      stored.authorityRevision === authorityRevision
    ) {
      selectedFamily = stored.family;
      selectionSource = 'COOKIE';
    }
    // A stale stored lens is no stored choice (see the workspace branch above): the request
    // falls through to the same automatic resolution, or the same chooser, as having no cookie.
  }

  const selected = selectedFamily ? byFamily.get(selectedFamily) : null;
  if (selectedFamily && !selected) selectionInvalid = true;
  if (!selected || selectionInvalid) {
    const communityOnly = lensOptions.every(
      (option) => option.firewallFamily === 'SAH' || option.firewallFamily === 'OTHER',
    );
    if (communityOnly && hint.source !== 'URL') {
      const defaultSelection =
        lensOptions.find((option) => option.family === 'SAH') ?? lensOptions[0]!;
      return {
        tenantId: input.tenantId,
        providerType: defaultSelection.firewallFamily,
        resolutionStatus: lensOptions.length === 2 ? 'RESOLVED_DUAL' : 'RESOLVED_MULTI',
        activeProgramFamily: defaultSelection.family,
        eligibleProgramFamilies,
        lensOptions,
        selectionRequired: false,
        selectionInvalid,
        selectionSource: 'AUTO',
        authorityRevision,
        contextRevision: `${authorityRevision}:${defaultSelection.family}`,
      };
    }
    const resolutionStatus = lensOptions.length === 2 ? 'RESOLVED_DUAL' : 'RESOLVED_MULTI';
    return {
      tenantId: input.tenantId,
      providerType: 'MIXED',
      resolutionStatus,
      activeProgramFamily: null,
      workspace: null,
      workspaceFamilies: [],
      admittedFirewalls: [],
      eligibleProgramFamilies,
      lensOptions,
      selectionRequired: true,
      selectionInvalid,
      selectionSource: selectionInvalid ? 'NONE' : selectionSource,
      authorityRevision,
      contextRevision: `${authorityRevision}:SHARED`,
    };
  }

  return {
    tenantId: input.tenantId,
    providerType: selected.firewallFamily,
    resolutionStatus: lensOptions.length === 2 ? 'RESOLVED_DUAL' : 'RESOLVED_MULTI',
    activeProgramFamily: selected.family,
    workspace: null,
    workspaceFamilies: [],
    admittedFirewalls: [],
    eligibleProgramFamilies,
    lensOptions,
    selectionRequired: false,
    selectionInvalid: false,
    selectionSource,
    authorityRevision,
    contextRevision: `${authorityRevision}:${selected.family}`,
  };
}

/**
 * Resolve the tenant's `orgType` to a `ProviderType` used by
 * `itemMatchesPrograms`. Unknown / null resolves to `UNKNOWN`; program-scoped
 * content stays absent until the tenant context is known. RBAC and module gates
 * remain authoritative, but they cannot substitute for program isolation.
 */
export function resolveProviderType(orgType: OrgType | string | null | undefined): ProviderType {
  switch (orgType) {
    case 'RESIDENTIAL':
      return 'RAC';
    case 'MIXED':
      return 'MIXED';
    default:
      return 'UNKNOWN';
  }
}

/**
 * Resolve tenant-level discovery scope from current program-enrollment tokens.
 * Org shape is intentionally absent: HOME_CARE can mean HCP, CHSP, NDIS or SAH
 * and therefore cannot authorise SAH presentation by itself.
 */
export function resolveProviderTypeFromProgramTokens(tokens: readonly unknown[]): ProviderType {
  return evaluateProviderProgramContext({
    tenantId: '00000000-0000-0000-0000-000000000000',
    rows: tokens.map((program_code, index) => ({ id: `token-${index}`, program_code })),
  }).providerType;
}
